/* ==========================================================================
   Midies — Cart reward ladder: pulse on a newly-crossed tier, and the
   in-drawer free-pair picker's "Add free pair" button.
   assets/cart-reward-ladder.js

   The ladder itself is pure Liquid and re-renders server-side on every cart
   change via Symmetry's merge system. This file adds:
     1. A one-time pulse on the 5/7/9 tick and a "pop" on the matching claim
        dot the moment a tier is crossed.
     2. The free-pair picker's add-to-cart: posts to Shopify's cart AJAX API,
        then fires the same `theme:cartchanged` event Symmetry's own
        quantity controls use, so <cart-form> refreshes itself normally.
   ========================================================================== */
(function () {
  'use strict';

  // tick order in the markup: 0 Start, 1 Free shipping, 2 = "5 pairs",
  // 3 = "7 pairs", 4 = "9 pairs". free_now 1/2/3 maps to ticks 2/3/4.
  var TICK_FOR_TIER = { 1: 2, 2: 3, 3: 4 };
  var lastFree = null;

  function checkPulse() {
    var el = document.querySelector('.crl');
    if (!el) return;

    var free = parseInt(el.getAttribute('data-crl-free'), 10) || 0;

    if (lastFree !== null && free > lastFree) {
      var ticks = el.querySelectorAll('.crl__tick');
      var t = ticks[TICK_FOR_TIER[free]];
      if (t) {
        t.classList.add('crl__tick--pulse');
        window.setTimeout(function () {
          t.classList.remove('crl__tick--pulse');
        }, 1400);
      }

      var claim = el.querySelector('.crl__claim[data-claim="' + free + '"]');
      if (claim) {
        claim.classList.add('crl__claim--pop');
        window.setTimeout(function () {
          claim.classList.remove('crl__claim--pop');
        }, 700);
      }
    }
    lastFree = free;
  }

  function start() {
    checkPulse();
    // Symmetry rewrites the ladder's attributes/markup on cart update.
    // Watch the whole drawer for those changes and re-check.
    var host = document.querySelector('.cart-drawer') ||
               document.querySelector('.cart-drawer-modal') ||
               document.body;
    try {
      new MutationObserver(checkPulse).observe(host, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['data-crl-free']
      });
    } catch (e) { /* no-op */ }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  /* ------------------------------------------------------------------------
     Free-pair picker — "Add free pair"
     Delegated on document so it keeps working after the drawer's markup is
     replaced by the merge system; no rebinding needed.
     ------------------------------------------------------------------------ */
  function pickerMessage(picker, text) {
    var msg = picker.querySelector('[data-crl-msg]');
    if (!msg) return;
    msg.textContent = text;
    msg.hidden = !text;
  }

  function cartRoute(name, fallbackPath) {
    if (window.theme && window.theme.routes && window.theme.routes[name]) {
      return window.theme.routes[name];
    }
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root.replace(/\/$/, '') + fallbackPath;
    }
    return fallbackPath;
  }
  function cartAddUrl() { return cartRoute('cart_add_url', '/cart/add.js'); }
  function cartChangeUrl() { return cartRoute('cart_change_url', '/cart/change.js'); }

  document.addEventListener('click', function (evt) {
    var btn = evt.target.closest('[data-crl-add]');
    if (!btn) return;

    var picker = btn.closest('.crl-picker');
    if (!picker) return;

    pickerMessage(picker, '');

    // find the checked size radio inside the currently-visible style/colour panel
    // (scoped to .crl-picker__size-input so a checked colour radio in the same
    // panel is never mistaken for the variant id)
    var checkedInputs = picker.querySelectorAll('.crl-picker__panel .crl-picker__size-input:checked');
    var variantId = null;
    for (var i = 0; i < checkedInputs.length; i++) {
      if (checkedInputs[i].offsetParent !== null) {
        variantId = checkedInputs[i].value;
        break;
      }
    }

    if (!variantId) {
      pickerMessage(picker, 'Pick a size first.');
      return;
    }

    btn.disabled = true;

    // If this exact variant already has a line in the cart, bump that line's
    // quantity via /cart/change.js instead of /cart/add.js. Once a BXGY
    // discount has split a variant's units across lines (a full-price line
    // + a $0 "free" line), Shopify's own /cart/add.js can no longer merge a
    // further add into either existing line — its key never matches theirs —
    // so it always spawns a brand-new 3rd line of the same variant. Explicitly
    // targeting the existing (non-discounted, "paid") line's key avoids that.
    fetch('/cart.js').
    then(function (r) { return r.json(); }).
    then(function (cart) {
      var vid = Number(variantId);
      var matches = cart.items.filter(function (item) { return item.variant_id === vid; });
      var target = null;
      for (var j = 0; j < matches.length; j++) {
        if (matches[j].line_price === matches[j].original_line_price) { target = matches[j]; break; }
      }
      if (!target && matches.length) target = matches[0];

      if (target) {
        return fetch(cartChangeUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: target.key, quantity: target.quantity + 1 })
        });
      }
      return fetch(cartAddUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });
    }).
    then(function (response) {
      return response.json().then(function (data) {
        return { ok: response.ok, data: data };
      });
    }).
    then(function (res) {
      if (!res.ok) {
        pickerMessage(picker, (res.data && res.data.description) || 'Sorry, that size just sold out — try another.');
        btn.disabled = false;
        return;
      }
      // Same event Symmetry's own cart controls dispatch after a change —
      // <cart-form> (data-ajax-update="true") picks it up and refreshes
      // itself via the merge system. The picker disappears on its own once
      // the reward ladder re-renders with the claim now met.
      document.documentElement.dispatchEvent(
        new CustomEvent('theme:cartchanged', { bubbles: true, cancelable: false })
      );
    }).
    catch(function () {
      pickerMessage(picker, 'Something went wrong adding that — try again.');
      btn.disabled = false;
    });
  });
})();
